import { useEffect, useRef } from 'react';
import { Clinic } from '../database';
import { GOOGLE_MAPS_API_KEY } from '../utils/googleMapsApiKey';

interface ClinicsMapProps {
    clinics: Clinic[];
    searchLocation?: { lat: number, lng: number } | null;
    className?: string;
}

declare global {
    interface Window {
        google: any;
    }
}

const ClinicsMap: React.FC<ClinicsMapProps> = ({ clinics, searchLocation, className = "h-[400px]" }) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const scriptLoadedRef = useRef<boolean>(false);

    useEffect(() => {
        // Load Google Maps script if not already loaded
        if (!window.google && !scriptLoadedRef.current) {
            scriptLoadedRef.current = true;
            const script = document.createElement('script');
            
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);

            script.onload = () => {
                console.log('Google Maps script loaded successfully');
                // Poczekaj chwilę żeby API się w pełni załadowało
                setTimeout(() => {
                    initializeMap();
                }, 500);
            };

            script.onerror = () => {
                console.error('Failed to load Google Maps script');
                scriptLoadedRef.current = false;
            };
        } else if (window.google && window.google.maps) {
            initializeMap();
        }

        return () => {
            // Cleanup markers when component unmounts
            markersRef.current.forEach(marker => marker.setMap(null));
            markersRef.current = [];
        };
    }, []);

    useEffect(() => {
        if (mapInstanceRef.current && clinics.length > 0) {
            updateMarkers();
        }
    }, [clinics]);

    useEffect(() => {
        if (mapInstanceRef.current && searchLocation && clinics.length > 0) {
            // Przybliż mapę do najbliższej kliniki (pierwszej w posortowanej liście)
            const nearestClinic = clinics[0];
            const center = new window.google.maps.LatLng(nearestClinic.latitude, nearestClinic.longitude);
            
            mapInstanceRef.current.setCenter(center);
            mapInstanceRef.current.setZoom(13); // Większe przybliżenie dla lepszego widoku
        }
    }, [searchLocation, clinics]);

    const initializeMap = () => {
        if (!mapRef.current) {
            console.log('Map ref not ready');
            return;
        }

        if (!window.google || !window.google.maps) {
            console.log('Google Maps not loaded yet, retrying in 100ms...');
            setTimeout(initializeMap, 100);
            return;
        }

        try {
            // Jeśli mamy lokalizację wyszukiwania, zawsze ją pokaż
            if (searchLocation) {
                const center = new window.google.maps.LatLng(searchLocation.lat, searchLocation.lng);
                
                mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                    zoom: 13,
                    center: center,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    styles: [
                        {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }]
                        }
                    ]
                });

                // Dodaj marker dla lokalizacji użytkownika
                const userMarker = new window.google.maps.Marker({
                    position: center,
                    map: mapInstanceRef.current,
                    title: 'Twoja lokalizacja',
                    icon: {
                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="8" fill="#3B82F6" stroke="white" stroke-width="2"/>
                                <circle cx="12" cy="12" r="3" fill="white"/>
                            </svg>
                        `),
                        scaledSize: new window.google.maps.Size(24, 24),
                        anchor: new window.google.maps.Point(12, 12)
                    }
                });

                // Dodaj info window dla lokalizacji użytkownika
                const userInfoWindow = new window.google.maps.InfoWindow({
                    content: `
                        <div class="p-2">
                            <h3 class="font-semibold text-blue-600">Twoja lokalizacja</h3>
                            <p class="text-sm text-gray-600">Wyszukiwana lokalizacja</p>
                        </div>
                    `
                });

                userMarker.addListener('click', () => {
                    userInfoWindow.open(mapInstanceRef.current, userMarker);
                });

                markersRef.current.push(userMarker);

                // Jeśli są kliniki, przybliż do najbliższej
                if (clinics.length > 0) {
                    const nearestClinic = clinics[0];
                    const clinicCenter = new window.google.maps.LatLng(nearestClinic.latitude, nearestClinic.longitude);
                    
                    // Ustaw centrum między lokalizacją użytkownika a najbliższą kliniką
                    const midLat = (searchLocation.lat + nearestClinic.latitude) / 2;
                    const midLng = (searchLocation.lng + nearestClinic.longitude) / 2;
                    const midCenter = new window.google.maps.LatLng(midLat, midLng);
                    
                    mapInstanceRef.current.setCenter(midCenter);
                    
                    // Dostosuj zoom żeby pokazać oba punkty
                    const bounds = new window.google.maps.LatLngBounds();
                    bounds.extend(center);
                    bounds.extend(clinicCenter);
                    mapInstanceRef.current.fitBounds(bounds);
                }
            } else if (clinics.length > 0) {
                // Standardowe centrowanie na wszystkich klinikach (gdy nie ma searchLocation)
                const bounds = new window.google.maps.LatLngBounds();
                clinics.forEach(clinic => {
                    bounds.extend(new window.google.maps.LatLng(clinic.latitude, clinic.longitude));
                });

                mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                    zoom: 12,
                    center: bounds.getCenter(),
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    styles: [
                        {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }]
                        }
                    ]
                });

                // Fit map to bounds
                mapInstanceRef.current.fitBounds(bounds);
            } else {
                // Fallback - centrum na Polsce
                const defaultCenter = new window.google.maps.LatLng(52.237049, 21.017532);
                
                mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                    zoom: 6,
                    center: defaultCenter,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    styles: [
                        {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }]
                        }
                    ]
                });
            }

            // Add clinic markers
            updateMarkers();
        } catch (error) {
            console.error('Error initializing map:', error);
        }
    };

    const updateMarkers = () => {
        if (!mapInstanceRef.current || !window.google || !window.google.maps) {
            console.log('Cannot update markers - map or Google Maps not ready');
            return;
        }

        try {
            // Usuń tylko markery klinik (zachowaj marker użytkownika)
            const clinicMarkers = markersRef.current.filter(marker => 
                marker.getTitle() !== 'Twoja lokalizacja'
            );
            clinicMarkers.forEach(marker => marker.setMap(null));
            
            // Usuń markery klinik z referencji
            markersRef.current = markersRef.current.filter(marker => 
                marker.getTitle() === 'Twoja lokalizacja'
            );

            // Add new clinic markers
            clinics.forEach((clinic, index) => {
                const marker = new window.google.maps.Marker({
                    position: { lat: clinic.latitude, lng: clinic.longitude },
                    map: mapInstanceRef.current,
                    title: clinic.name,
                    animation: window.google.maps.Animation.DROP,
                    // Dodaj numer do markera najbliższej kliniki
                    label: index === 0 ? "1" : undefined
                });

                // Create info window content
                const content = `
                    <div class="p-2">
                        <h3 class="font-semibold text-gray-800">${clinic.name}</h3>
                        <p class="text-sm text-gray-600">${clinic.address}</p>
                        <p class="text-sm text-gray-600">${clinic.postal_code} ${clinic.city}</p>
                        <p class="text-sm text-gray-600">${clinic.phone_number}</p>
                        ${clinic.distance ? `<p class="text-sm text-blue-600 font-medium">Odległość: ${clinic.distance.toFixed(1)} km</p>` : ''}
                        <a href="${clinic.website_url}" target="_blank" rel="noopener noreferrer" 
                           class="text-sm text-blue-600 hover:underline mt-2 inline-block">
                            Visit Website
                        </a>
                    </div>
                `;

                const infoWindow = new window.google.maps.InfoWindow({
                    content: content
                });

                // Add click listener to marker
                marker.addListener('click', () => {
                    infoWindow.open(mapInstanceRef.current, marker);
                });

                markersRef.current.push(marker);
            });
        } catch (error) {
            console.error('Error updating markers:', error);
        }
    };

    return (
        <div 
            ref={mapRef} 
            className={`w-full rounded-lg shadow-md border border-gray-200 ${className}`}
        />
    );
};

export default ClinicsMap; 